import React, { Component, PropTypes } from 'react';
import _ from 'lodash';
import { Row, Col } from 'react-bootstrap';
import { startOfToday, startOfWeek, startOfMonth, startOfYear, subWeeks, subMonths, subYears } from 'date-fns';
import { readComponentInstanceResourceList } from 'lib/frankly';
import { buildQueryString } from 'utils/UrlUtils';
import ArticleList from 'components/news/ArticleList';
import translate from 'utils/translate';

const ResultTypeValues = [
  { value: '', text: 'All' },
  { value: '1', text: 'Story' },
  { value: '2', text: 'Video' }
];

const DateValues = [{
    text: 'Anytime',
    value: ''
  },
  {
    text: 'Today',
    value: startOfToday().toISOString()

  },
  {
    text: 'Past Week',
    value: startOfWeek(subWeeks(startOfToday(), 1), { weekStartsOn: 1 }).toISOString()
  },
  {
    text: 'Past Month',
    value: startOfMonth(subMonths(startOfToday(), 1)).toISOString()
  },
  {
    text: 'Past Year',
    value: startOfYear(subYears(startOfToday(), 1)).toISOString()
  }
];

const defaultFilterOptions = {
  type: {
    label: 'Result Type',
    selectedValue: '',
    savedOptions: []
  },
  topic: {
    label: 'Topic',
    selectedValue: '',
    savedOptions: []
  },
  byline: {
    label: 'Byline',
    selectedValue: '',
    savedOptions: []
  },
  dateline: {
    label: 'Dateline',
    selectedValue: '',
    savedOptions: []
  },
  datetime: {
    label: 'Last Edited',
    selectedValue: '',
    savedOptions: []
  }
};

class SearchResult extends Component {
  static propTypes = {
    showBylines: PropTypes.bool,
    showThumbnails: PropTypes.bool,
    thumbnailAlignment: PropTypes.string,
    showDescriptions: PropTypes.bool,
    textColor: PropTypes.string,
    searchOptions: PropTypes.shape({
      affiliateIds: PropTypes.array
    })
  }

  static contextTypes = {
    config: PropTypes.object.isRequired
  }

  static defaultProps = {
    showBylines: true,
    showThumbnails: false,
    thumbnailAlignment: 'right',
    showDescriptions: true,
    textColor: '#000',
  }

  state = {
    page: 1,
    numberOfResultsPerPage: this._getParams().num || 5,
    keyword: this._getParams().qu || '',
    newKeyword: this._getParams().qu || '',
    showFilter: false,
    filterHistory: [],
    filterOptions: _.cloneDeep(defaultFilterOptions)
  }

  componentWillMount() {
    this._getData(this._prepareSearchString());
    this._loadMore = _.debounce(this._loadMore, 1000);
    this._setSearchText(this.state.keyword);
  }

  _setSearchText(keyword) {
    this.setState({
      keyword
    });
  }

  _loadMore() {
    const {
      page,
      numberOfResultsPerPage
    } = this.state;
    const queryString = `${this._prepareSearchString()}&start=${numberOfResultsPerPage * page + 1}`;

    this._getData(queryString, page + 1, true);
  }

  _prepareSearchString(newFilterOptions, qu) {
    const {
      config: {
        affiliate: {
          id = ''
        } = {}
      } = {}
    } = this.context;

    const {
      searchOptions: {
        affiliateIds = []
      }
    } = this.props;
    const { numberOfResultsPerPage, keyword, filterOptions: currentFilterOptions = {} } = this.state;
    const filterOptions = newFilterOptions ? newFilterOptions : currentFilterOptions;
    const { type = '', topic = '', byline = '', dateline = '', datetime = '' } = _.mapValues(filterOptions, item => item.selectedValue);
    const searchParams = {
      affiliateno: id,
      resulttype: type,
      qu: encodeURIComponent(qu || keyword),
      num: numberOfResultsPerPage,
      lastediteddatemin: datetime,
      topic,
      bl: byline,
      dateline
    };
    const options = { encode: true, strict: true };
    return buildQueryString(searchParams, options, _.uniqBy(affiliateIds));
  }

  _getParams() {
    const searchString = location.search.substring(1);
    const params = {};

    searchString
      .split('&')
      .map((param) => {
        const [key, value] = param.split('=');

        params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';

        return params;
      }, {});

    return params;
  }

  _getData(searchString, page = 1, loadMore = false) {
    readComponentInstanceResourceList(this, { propPath: `searchOptions`, vars: { params: searchString } }).then(rawResponse => {
      const {
        data: {
          SEARCH: {
            RESULTS: {
              NUMRESULTS: totalNumberOfResults = 0,
              RESULT: newResultList = []
            } = {},
            NAVIGATION: {
              NAVIGATIONENTRY: navigationEntry = []
            } = {}
          } = {}
        } = {}
      } = rawResponse[0];

      const { searchResults = [] } = this.state;
      const resultList = _.isArray(newResultList) ? newResultList : [newResultList];
      const convertedResultList = _.map(resultList, (item) => this._convertJsonData(item));

      if (loadMore) {
        convertedResultList.unshift(...searchResults);
      }

      this.setState({
        searchResults: convertedResultList,
        page,
        totalNumberOfResults,
        navigationEntry
      });
    }).catch(() => {
      console.log('Error while getting data');
    });
  }

  _onChangeFilter(key, event) {
    const value = event.target.value;
    const { filterHistory, filterOptions } = this.state;
    let newFilterHistory = filterHistory;

    filterOptions[key].selectedValue = value;

    if (filterOptions[key].savedOptions.length) {
      let index = filterHistory.findIndex(item => item === key);

      if (index !== -1) {
        index = value ? index + 1 : index;
        const firstValues = filterHistory.slice(0, index);
        const afterValues = filterHistory.slice(index);

        afterValues.forEach(item => {
          filterOptions[item].selectedValue = '';
          filterOptions[item].savedOptions = [];
        });

        newFilterHistory = firstValues;
      }
    } else {
      filterOptions[key].savedOptions = this._getStaticFilterOption(key) || this._getDynamicFilterOption(key);

      filterHistory.push(key);
    }

    this.setState({
      filterHistory: newFilterHistory,
      filterOptions
    });

    this._getData(this._prepareSearchString(filterOptions));
  }

  _onSearchKeyEnter(event) {
    if (event.key === 'Enter') {
      this._onSearchButtonClick();
    }
  }

  _onSearchButtonClick() {
    const newKeyword = this.state.newKeyword;
    this._getData(this._prepareSearchString(defaultFilterOptions, newKeyword));
    this._setSearchText(newKeyword);
    this._resetFilter();
  }

  _onChangeSearchInput(event) {
    this.setState({
      newKeyword: event.target.value
    });
  }

  _toggleFilter() {
    this.setState({ showFilter: !this.state.showFilter });
  }

  _resetFilter() {
    this.setState({
      filterHistory: [],
      filterOptions: _.cloneDeep(defaultFilterOptions)
    });
  }

  _clearFilter() {
    this._resetFilter();
    this._getData(this._prepareSearchString(defaultFilterOptions));
  }

  _buildDate(date) {
    return _.values(date).join(' ');
  }

  _convertJsonData(item) {
    const {
      HEADLINE: headline = '',
      BYLINES: byLines = [],
      ABSTRACTIMAGE: { FILENAME: newsImage = '' } = {},
      SEARCHRESULTTYPE: resultType = '',
      TITLE: id = '',
      SEO: { PAGEURL: slug = '' } = {},
      CREATIONDATE: creationDate = {},
      LASTEDITEDDATE: lastEditedDate = {},
      DESCRIPTION: previewText = ''
    } = item;

    const bylines = _.map(byLines, byline => {
      const { FIRSTNAME: firstname = '', LASTNAME: lastname = '' } = byline;
      return { firstname, lastname };
    });

    const featureType = resultType === '1' ? 'story' : 'clip';

    return {
      headline,
      bylines,
      publishedDate: this._buildDate(creationDate),
      updatedDate: this._buildDate(lastEditedDate),
      abstract: previewText,
      contentClassification: '',
      abstractimage: { filename: newsImage },
      surfaceable: [{ type: featureType }],
      id,
      type: featureType,
      seo: { pageurl: slug }
    };
  }

  _getDynamicFilterOption(name) {
    const { navigationEntry = [] } = this.state;
    const entry = navigationEntry.find(item => _.includes(name, _.toLower(item['@NAME']))) || {};
    const { config: { appOptions: {language = 'en'} } } = this.context;
    let {
      NAVIGATIONELEMENT: elements = []
    } = entry;

    if (!_.isArray(elements)) {
      elements = [elements];
    }

    const filterOptions = elements.map(item => {
      const itemName = item['@NAME'];

      return {
        text: itemName,
        value: itemName
      };
    });

    filterOptions.unshift({ text: translate('All', language), value: '' });

    return filterOptions;
  }

  _getStaticFilterOption(key) {
    const options = {
      type: ResultTypeValues,
      datetime: DateValues
    }[key];

    const { config: { appOptions: {language = 'en'} } } = this.context;

    if (options) { // Translate filter option text
      options.map((option, index) => {
        options[index]['text'] = translate(option.text, language) || option.text;
      });
    }

    return options;
  }

  render() {
    const {
      showBylines,
      showThumbnails,
      thumbnailAlignment,
      showDescriptions,
      textColor,
    } = this.props;

    const {
      searchResults,
      page,
      numberOfResultsPerPage,
      totalNumberOfResults = 0,
      keyword,
      newKeyword,
      showFilter,
      filterOptions,
    } = this.state;

    const dropdownColWidths = {
      xs: 12,
      md: 2
    };

    const remaining = totalNumberOfResults - numberOfResultsPerPage * page;
    const showing = Math.min(totalNumberOfResults, numberOfResultsPerPage * page) || 0;

    const { config: { appOptions: {language = 'en'} } } = this.context;
    const longTranslatedText = {
      title: `${translate('Search', language)} ${translate('Results', language)}`,
      showing: `${translate('Showing', language)} ${showing} ${translate('of', language)} ${totalNumberOfResults} ${translate('for', language)} <b>${keyword}</b>`,
      placeholder: `${translate('Enter keywords here', language)}...`
    };

    const listProp = {
      showReadMore: false,
      showTitle: false,
      numberOfItems: showing,
      showBylines,
      showThumbnails,
      thumbnailAlignment,
      showDescriptions,
      textColor
    };

    const FRN_rawResponses = [{ data: { features: searchResults } }];

    let filterContent;

    if (showFilter) {
      filterContent = (
        Object.keys(filterOptions).map((key, i) => {
          const { label, selectedValue, savedOptions } = filterOptions[key];
          const displayFilterOptions = this._getStaticFilterOption(key) || (savedOptions.length ? savedOptions : this._getDynamicFilterOption(key));
          const translatedLabel = translate(label, language); //Translate label for option

          return (
            <Col key={i} {...dropdownColWidths} className="form-group">
              <select value={selectedValue || label} className="form-control" onChange={(event) => this._onChangeFilter(key, event)}>
                {
                  !selectedValue && <option key={label} hidden disabled value={label}>{translatedLabel}</option>
                }
                  {
                    displayFilterOptions.map((option, j) => {
                      const { text = '', value = ''} = option;
                      return <option key={j} value={value}>{text}</option>;
                    })
                  }
              </select>
            </Col>
          );
        })
      );

      filterContent.push(
        <Col {...dropdownColWidths} className="form-group">
          <button className="SearchResult-clearButton" onClick={() => this._clearFilter()}>
            {translate('Clear', language)}
            <i className="fa fa-times" aria-hidden="true"></i></button>
        </Col>
      );
    }

    return (
      <div className="SearchResult" >
        <h2>{longTranslatedText.title}</h2>
        <p dangerouslySetInnerHTML={{ __html: longTranslatedText.showing }} />
        <Row>
          <Col xs={12} className="form-group">
            <input ref="searchInput"
              className="form-control SearchResult-searchInput"
              placeholder={longTranslatedText.placeholder}
              value={newKeyword}
              onKeyPress={(event) => this._onSearchKeyEnter(event)}
              onChange={(event) => this._onChangeSearchInput(event)}
              />
            <button className="SearchResult-btn SearchResult-btn--search" onClick={() => this._onSearchButtonClick()}>
              {translate('Search', language)}
            </button>
            <button className="SearchResult-btn SearchResult-btn--advanced" onClick={() => this._toggleFilter()}>
              {translate('Advanced', language)}
            </button>
          </Col>
          {filterContent}
        </Row>
        <ArticleList FRN_rawResponses={FRN_rawResponses} {...listProp}
        />
        {remaining > 0 ?
          <div className="SearchResult-more">
            <span onClick={() => this._loadMore()}>
              {translate('Load', language)} {Math.min(remaining, numberOfResultsPerPage)} {translate('more results', language)}
            </span>
          </div>
        : null
        }
      </div>
    );
  }
}

export default SearchResult;



// WEBPACK FOOTER //
// ./app/components/search/SearchResult.js